import {ActionList} from '@primer/react'
import React, {forwardRef, useState} from 'react'

import type {Items} from '../types'
import {SelectItem} from './SimpleSelectItem'

interface SelectBodyProps {
  selectionVariant: 'single' | 'multiple'
  items: Items[]
  onSelect?: (selectedItem: Items, items?: Items[]) => void
  controlled?: (items: Items[]) => void
  showGroupHeading?: boolean
}

export const SelectBody = forwardRef<HTMLLIElement, SelectBodyProps>(
  ({selectionVariant, items, onSelect, controlled, showGroupHeading}, ref) => {
    const [selectedItems, setSelectedItems] = useState<Items[]>(() => items.filter(item => item.selected))
    const hasGroupId = items.find(item => item.groupId !== undefined)

    const groups = hasGroupId
      ? items.reduce(
          (options, item) => {
            if (item.groupId !== undefined) {
              options[item.groupId] = options[item.groupId] || []
              if (options[item.groupId]) (options[item.groupId] as Items[]).push(item)
            }
            return options
          },
          {} as {[key: number]: Items[]},
        )
      : null

    const itemsWithoutGroup = items.filter(item => item.groupId === undefined)
    const groupCollection = groups && Object.entries(groups)

    const onSelectToggle = (selectedItem: Items) => {
      const {id} = selectedItem
      if (onSelect) onSelect(selectedItem)

      if (controlled) {
        const isSelected = selectedItems.find(item => item.id === id)
        if (!isSelected) {
          const itemSelected = selectionVariant === 'multiple' ? [...selectedItems, selectedItem] : [selectedItem]
          setSelectedItems(itemSelected)
          controlled(itemSelected)
        } else {
          const refreshedItems = selectedItems.filter(item => item.id !== id)
          setSelectedItems(refreshedItems)
          controlled(refreshedItems)
        }
      }
    }

    return (
      <>
        {groups ? (
          <>
            {groupCollection
              ? groupCollection.map(([groupId, groupItems], index) => (
                  <React.Fragment key={groupId}>
                    <ActionList.Group
                      key={groupId}
                      aria-label={groupItems[0]?.groupTitle || `Group ${groupId}`}
                      selectionVariant={selectionVariant}
                    >
                      {showGroupHeading ? (
                        <ActionList.GroupHeading variant="filled">
                          {groupItems[0]?.groupTitle || `Group ${groupId}`}
                        </ActionList.GroupHeading>
                      ) : null}
                      {groupItems.map((props, itemIndex) => (
                        <SelectBodyItem
                          key={props.id}
                          selectedItems={selectedItems}
                          onSelectToggle={onSelectToggle}
                          controlled={controlled}
                          ref={itemIndex === 0 && index === 0 ? ref : undefined}
                          {...props}
                        />
                      ))}
                    </ActionList.Group>
                    {groupCollection.length > 1 && index !== groupCollection.length - 1 ? <ActionList.Divider /> : null}
                  </React.Fragment>
                ))
              : null}

            {itemsWithoutGroup.map(props => (
              <SelectBodyItem
                key={props.id}
                selectedItems={selectedItems}
                onSelectToggle={onSelectToggle}
                controlled={controlled}
                {...props}
              />
            ))}
          </>
        ) : (
          <>
            {items.map((props, itemIndex) => (
              <SelectBodyItem
                key={props.id}
                selectedItems={selectedItems}
                onSelectToggle={onSelectToggle}
                controlled={controlled}
                ref={itemIndex === 0 ? ref : null}
                {...props}
              />
            ))}
          </>
        )}
      </>
    )
  },
)

interface ItemProps extends Items {
  selectedItems: Items[]
  onSelectToggle: (selectedItem: Items) => void
  controlled?: (items: Items[]) => void
  onSelect?: (selectedItem: Items, items?: Items[]) => void
}

const SelectBodyItem = React.forwardRef<HTMLLIElement, ItemProps>(
  ({id, label, description, selectedItems, onSelectToggle, onSelect, controlled, selected, ...props}, ref) => {
    const onSelection = () => {
      onSelectToggle({
        label,
        id,
        selected,
        description,
        descriptionVariant: props.descriptionVariant,
      })
    }

    return (
      <SelectItem
        label={label}
        id={id}
        description={description}
        descriptionVariant={props.descriptionVariant}
        selected={!controlled ? Boolean(selected) : selectedItems.some(item => item.id === id)}
        onSelect={onSelect || onSelection}
        className={props.className}
        renderItem={props.renderItem}
        ref={ref}
        trailingVisual={props.trailingVisual}
        disabled={props.disabled}
        renderDescription={props.renderDescription}
      />
    )
  },
)

SelectBody.displayName = 'SelectBody'
SelectBodyItem.displayName = 'SelectBodyItem'
